/**
 * ConnectionManager Configuration Constants
 *
 * Retry and reconnection settings for SIP connection management.
 * These values mirror the legacy sipConnectionRecoveryService configuration.
 */

/**
 * Reconnection retry options interface
 */
export interface ReconnectRetryOptions {
  /** Maximum number of reconnection attempts before giving up */
  maxAttempts?: number;

  /** Initial delay in milliseconds before first retry */
  initialDelay?: number;

  /** Maximum delay in milliseconds between retries */
  maxDelay?: number;

  /** Exponential backoff multiplier (e.g., 2 for 2x growth per attempt) */
  backoffFactor?: number;

  /** Enable automatic retry with backoff (default: true). Set to false for manual reconnects to prevent multiple automatic attempts */
  enableRetry?: boolean;
}

/**
 * Reconnection retry configuration defaults
 * Based on legacy sipConnectionRecoveryService defaults
 */
export const RECONNECT_RETRY_CONFIG = {
  /** Maximum number of reconnection attempts before giving up */
  MAX_ATTEMPTS: 10,

  /** Initial delay in milliseconds before first retry (0.5 seconds) */
  INITIAL_DELAY: 500,

  /** Maximum delay in milliseconds between retries (16 seconds) */
  MAX_DELAY: 16000,

  /** Exponential backoff multiplier (2x growth per attempt) */
  BACKOFF_FACTOR: 2,

  /** Add random jitter to prevent thundering herd problem */
  JITTER: true,

  /** Timeout in milliseconds for each reconnection attempt (20 seconds) */
  TIMEOUT: 20000,
} as const;

/**
 * Delay in milliseconds to wait after cleanup before reinitializing
 * Allows SIP.js library to properly close WebSocket connections and clean up internal state
 */
export const CLEANUP_WAIT_DELAY_MS = 500;

/**
 * Delay in milliseconds before showing connection loss UI and playing audio.
 * Mirrors legacy PhoneStore.CONNECTION_LOSS_FEEDBACK_DELAY (5 seconds).
 * Avoids false positives from brief network blips.
 */
export const CONNECTION_LOSS_FEEDBACK_DELAY_MS = 5000;

/**
 * Hard timeout in milliseconds before dropping an active call when network cannot be restored.
 * After this window, all active/incoming calls are terminated via network termination.
 * The timer resets on each reconnection attempt to give a fresh 13-second window.
 */
export const CALL_DROP_TIMEOUT_MS = 13_000;
